import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Chart } from 'react-chartjs-2';
import { selectCashierData } from '../reducer';
import { FETCH_CASHIER_TRACKING, fetchCashierTrackingData } from '../actions';
import { selectApiRequests } from '../../../store/apiRequestReducer';
import { selectCoreSiteId } from '../../settings/reducer';
import { useIntl } from 'react-intl';
import Loader from '../../../core/components/Loader';
import { OVERVIEW_YELLOW } from '../containers/Charts';

interface CashierTrackingRow {
  site_id: number;
  cashier: string | null;
  rewash: number;
  capture: number;
  claim: number;
  [key: string]: any;
}

export const CashierTrackingGraph = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const siteId = useSelector(selectCoreSiteId);
  const cashierData = useSelector(selectCashierData);
  const cashierDataLoading = useSelector((state) => {
    const req = selectApiRequests(state)[FETCH_CASHIER_TRACKING];
    return !req || req.isLoading;
  });

  const getCashierLabels = (data: CashierTrackingRow[]): string[] => {
    return data.map((item: CashierTrackingRow) => {
      const nameParts = item.cashier?.split(' ') ?? [];
      const lastName = nameParts[nameParts.length - 1];
      if (nameParts.length >= 2 && lastName.length > 0) {
        return `${nameParts[0]} ${lastName[0].toUpperCase()}.`;
      }
      return item.cashier ?? '';
    });
  };

  const chartData: any = useMemo(() => {
    const sortedChartData = [...cashierData]
      .sort((a: CashierTrackingRow, b: CashierTrackingRow) => {
        return b.capture - a.capture;
      })
      .slice(0, 5);
    return {
      datasets: [
        {
          type: 'bar',
          label: intl.formatMessage({ id: 'capturePercentage' }),
          data: sortedChartData.map((r: CashierTrackingRow) => r.capture),
          backgroundColor: 'rgb(59, 130, 246)',
          borderRadius: 6,
          yAxisID: 'y',
          order: 3,
        },
        {
          type: 'line',
          label: intl.formatMessage({ id: 'rewashPercentage' }),
          data: sortedChartData.map((r: CashierTrackingRow) => r.rewash),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgb(239, 68, 68)',
          yAxisID: 'y1',
          tension: 0.4,
          order: 1,
        },
        {
          type: 'line',
          label: intl.formatMessage({ id: 'claimPercentage' }),
          data: sortedChartData.map((r: CashierTrackingRow) => r.claim),
          borderColor: OVERVIEW_YELLOW,
          backgroundColor: OVERVIEW_YELLOW,
          yAxisID: 'y1',
          tension: 0.4,
          order: 2,
        },
      ],
      labels: getCashierLabels(sortedChartData),
    };
  }, [cashierData]);
  const chartOptions: any = {
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false, // only want the grid lines for one axis to show up
        },
      },
    },
  };
  useEffect(() => {
    if (siteId) {
      dispatch(fetchCashierTrackingData());
    }
  }, [siteId]);
  return (
    <section className="cashier-tracking overview-chart-card">
      <p className="chart-title">{intl.formatMessage({ id: 'cashierPerformanceKPIs' })}</p>
      {cashierDataLoading ? (
        <Loader />
      ) : (
        <Chart type={'bar'} data={chartData} options={chartOptions} />
      )}
    </section>
  );
};
